import React, { useState, useEffect, useRef } from "react";
import { Form, FormGroup, Input, Button, Row, Col } from "reactstrap";
import LaddaButton, { EXPAND_RIGHT } from "react-ladda";
import PropTypes from "prop-types";

import { KYCConstants, getAccountType } from "../types";
import { CustomFormInput, SelectFormField } from "../../../../utils/FormFields";
import { CircularProgress } from "../../../..";
import { AccountMode, showToast } from "../../../../utils/Helper";
import { NumberFormatInput } from "../../../NumberFormatInput/NumberFormatInput";

const KYCTab = ({ KYC, accountId, account, setAccountData, isReadOnly, services, mode }) => {
  const [loadingSave, setLoadingSave] = useState(false);
  const [changedKYC, setChangedKYC] = useState({});
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const valueref = useRef();
  const valuerefnew = useRef();
  const accountref = useRef();

  useEffect(() => {
    if (!KYC) {
      const body = {
        AccountGetInfo: {
          GetAccountDataOnboardingId: account.AccountOnboardingId,
          GetAccountDataId: accountId,
          GetAccountDataType: getAccountType.KYC,
        },
      };
      if (mode === AccountMode.New) {
        services.fetchNewAccountInfo(body, (data) => {
          if (data && data.SDTAccount) {
            setAccountData({ AccountId: account.AccountOnboardingId, KYC: data.SDTAccount.KYC }, true);
          }
        });
      } else {
        services.fetchAccountSectorInfo(body, (data) => {
          if (data && data.SDTAccount) {
            setAccountData({ AccountId: accountId, KYC: data.SDTAccount.KYC }, false);
          }
        });
      }
    }
  }, [setAccountData, KYC, accountId, services]);

  useEffect(() => {
    if (services.getParameterValue){
      services.getParameterValue({ ParameterName: "useKYCReport" }, (res) => {
        if (res &&res.SdtParametersData && res.SdtParametersData.ParameterNumericValue) {
          setShowDownload(res.SdtParametersData.ParameterNumericValue == 1);
        }
      })
    }
  }, [services]);

  const handleSave = () => {
    const changedKYCcurrent = valuerefnew.current;
    const newKYC = { ...valueref.current };
    Object.keys(changedKYCcurrent).forEach((key) => {
      newKYC[key] = changedKYCcurrent[key];
    });
    const body = {
      account: {
        AccountId: accountId,
        AccountOnboardingId: accountId,
        KYC: newKYC,
      },
      accountChangeType: getAccountType.KYC,
    };
    setLoadingSave(true);
    if (mode === AccountMode.New) {
      services.fetchNewAccount(body, (res) => {
        if (res) {
          setAccountData(
            {
              AccountId: account.AccountOnboardingId,
              KYC: newKYC,
            },
            true
          );
          setChangedKYC({});
          setLoadingSave(false);
          showToast("Changes saved successfully");
        } else {
          setLoadingSave(false);
        }
      });
    } else {
      services.fetchAccountUpdate(body, ({ result }) => {
        if (result && result.Code === 200) {
          setAccountData(
            {
              AccountId: accountId,
              KYC: newKYC,
            },
            true
          );
          setChangedKYC({});
          setLoadingSave(false);
        } else {
          setLoadingSave(false);
        }
      });
    }
  };

  const onChange = (name, value) => {
    if (!loadingSave) {
      const newKYC = { ...changedKYC };
      const KYCupd = { ...KYC, ...changedKYC };
      if (KYC[name] !== value) {
        newKYC[name] = value;
      } else if (newKYC[name]) {
        newKYC[name] = undefined;
      }
      setChangedKYC(newKYC);
      setAccountData({
        KYC: KYCupd,
      });
    }
  };

  useEffect(() => {
    return () => {
      if (mode === AccountMode.New) {
        handleSave();
      }
    };
  }, []);

  
const getKYCReport = () => {
  setDownloading(true);
  const body = {
    AccountId: accountId,
  };
  try{
    services.downloadKYCReport(body).then((res)=> {
      if (res){
        setFile(res.response.url);
        setDownloading(false);
      }else{
        setDownloading(false);
        showToast("Error downloading report", true)
      }
    })
  }catch(e){
    setDownloading(false);
    showToast("Error downloading report", true)
  }
};


  useEffect(() => {
    valuerefnew.current = changedKYC;
  }, [changedKYC]);

  useEffect(() => {
    valueref.current = KYC;
  }, [KYC]);

  useEffect(() => {
    accountref.current = account;
  }, [account]);

  const revert = () => setChangedKYC({});

  if (!KYC) {
    return <CircularProgress sm />;
  }

  return (
    <div>
      <Form>
        <FormGroup tag="fieldset">
          <Row>
            <Col>
              <SelectFormField
                label="Stock Market Knowledge: "
                value={changedKYC.AccountKnowledgeStockMarket || KYC.AccountKnowledgeStockMarket}
                name="AccountKnowledgeStockMarket"
                onChange={onChange}
                selectItems={Object.keys(KYCConstants.StockMarketKnowledge)}
                getSelectItemId={(i) => KYCConstants.StockMarketKnowledge[i]}
                getSelectItemName={(i) => KYCConstants.StockMarketKnowledge[i]}
                disabled={isReadOnly}
              />
            </Col>
            <Col>
              <SelectFormField
                label="Invest Bonds: "
                value={changedKYC.AccountInvestBonds || KYC.AccountInvestBonds}
                name="AccountInvestBonds"
                onChange={onChange}
                selectItems={[true, false]}
                getSelectItemId={(i) => i}
                getSelectItemName={(i) => (i ? "Yes" : "No")}
                disabled={isReadOnly}
              />
            </Col>
            <Col>
              <SelectFormField
                label="Invest Stocks: "
                value={changedKYC.AccountInvestStocks || KYC.AccountInvestStocks}
                name="AccountInvestStocks"
                onChange={onChange}
                selectItems={[true, false]}
                getSelectItemId={(i) => i}
                getSelectItemName={(i) => (i ? "Yes" : "No")}
                disabled={isReadOnly}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <CustomFormInput label="Invest Funds: ">
                <Input
                  type="select"
                  value={changedKYC.AccountInvestFunds || KYC.AccountInvestFunds}
                  onChange={(e) => onChange("AccountInvestFunds", e.target.value)}
                  disabled={isReadOnly}
                >
                  <option value>Yes</option>
                  <option value={false}>No</option>
                </Input>
              </CustomFormInput>
            </Col>
            <Col>
              <CustomFormInput label="Invest Others: ">
                <Input
                  type="select"
                  value={changedKYC.AccountInvestOthers || KYC.AccountInvestOthers}
                  onChange={(e) => onChange("AccountInvestOthers", e.target.value)}
                  disabled={isReadOnly}
                >
                  <option value>Yes</option>
                  <option value={false}>No</option>
                </Input>
              </CustomFormInput>
            </Col>
            <Col>
              <CustomFormInput label="Customer Link: ">
                <Input
                  type="select"
                  value={changedKYC.AccountKYCCustomerLink || KYC.AccountKYCCustomerLink}
                  onChange={(e) => onChange("AccountKYCCustomerLink", e.target.value)}
                  disabled={isReadOnly}
                >
                  {Object.keys(KYCConstants.CustomerLink).map((key) => (
                    <option key={key} value={key}>
                      {KYCConstants.CustomerLink[key]}
                    </option>
                  ))}
                </Input>
              </CustomFormInput>
            </Col>
          </Row>
          <Row>
            <Col>
              <CustomFormInput label="Customer Link Specify: ">
                <Input
                  value={
                    changedKYC.AccountKYCCustomerLinkSpecify ||
                    changedKYC.AccountKYCCustomerLinkSpecify === ""
                      ? changedKYC.AccountKYCCustomerLinkSpecify
                      : KYC.AccountKYCCustomerLinkSpecify
                  }
                  onChange={(e) => onChange("AccountKYCCustomerLinkSpecify", e.target.value)}
                  disabled={isReadOnly}
                />
              </CustomFormInput>
            </Col>
            <Col>
              <CustomFormInput label="Related Since: ">
                <Input
                  type="select"
                  value={changedKYC.AccountKYCCustomerRelatedSince || KYC.AccountKYCCustomerRelatedSince}
                  onChange={(e) => onChange("AccountKYCCustomerRelatedSince", e.target.value)}
                  disabled={isReadOnly}
                >
                  {Object.keys(KYCConstants.CustomerRelatedSince).map((key) => (
                    <option key={key} value={KYCConstants.CustomerRelatedSince[key]}>
                      {KYCConstants.CustomerRelatedSince[key]}
                    </option>
                  ))}
                </Input>
              </CustomFormInput>
            </Col>
            <Col>
              <CustomFormInput label="Customer Profession: ">
                <Input
                  disabled={isReadOnly}
                  value={changedKYC.AccountKYCCustomerProfessionName || KYC.AccountKYCCustomerProfessionName}
                  onChange={(e) => onChange("AccountKYCCustomerProfessionName", e.target.value)}
                />
              </CustomFormInput>
            </Col>
          </Row>
          <Row>
            <Col>
              <CustomFormInput label="Anual Income: ">
                <NumberFormatInput
                  disabled={isReadOnly}
                  type="text"
                  customInput={Input}
                  value={
                    changedKYC.AccountKYCAnualIncome || changedKYC.AccountKYCAnualIncome === ""
                      ? changedKYC.AccountKYCAnualIncome
                      : KYC.AccountKYCAnualIncome
                  }
                  onValueChange={(e) => onChange("AccountKYCAnualIncome", e.value)}
                  dataTypeDecimalName="currency"
                />
              </CustomFormInput>
            </Col>
            <Col>
              <CustomFormInput label="Estimated Amount: ">
                <NumberFormatInput
                  disabled={isReadOnly}
                  type="text"
                  customInput={Input}
                  value={
                    changedKYC.AccountKYCEstimatedAmount || changedKYC.AccountKYCEstimatedAmount === ""
                      ? changedKYC.AccountKYCEstimatedAmount
                      : KYC.AccountKYCEstimatedAmount
                  }
                  onValueChange={(e) => onChange("AccountKYCEstimatedAmount", e.value)}
                  dataTypeDecimalName="currency"
                />
              </CustomFormInput>
            </Col>
            <Col>
              <CustomFormInput label="Amount Deposited Assets: ">
                <NumberFormatInput
                  disabled={isReadOnly}
                  type="text"
                  customInput={Input}
                  value={
                    changedKYC.AccountKYCAmountDepositedStocks ||
                    changedKYC.AccountKYCAmountDepositedStocks === ""
                      ? changedKYC.AccountKYCAmountDepositedStocks
                      : KYC.AccountKYCAmountDepositedStocks
                  }
                  onValueChange={(e) => onChange("AccountKYCAmountDepositedStocks", e.value)}
                  dataTypeDecimalName="currency"
                />
              </CustomFormInput>
            </Col>
          </Row>
          <Row>
            <Col>
              <CustomFormInput label="Amount Withdraw Assets:">
                <NumberFormatInput
                  disabled={isReadOnly}
                  type="text"
                  customInput={Input}
                  value={
                    changedKYC.AccountKYCAmountRetiredStocks ||
                    changedKYC.AccountKYCAmountRetiredStocks === ""
                      ? changedKYC.AccountKYCAmountRetiredStocks
                      : KYC.AccountKYCAmountRetiredStocks
                  }
                  onValueChange={(e) => onChange("AccountKYCAmountRetiredStocks", e.value)}
                  dataTypeDecimalName="currency"
                />
              </CustomFormInput>
            </Col>
            <Col>
              <CustomFormInput label="Quantity Deposited Assets: ">
                <NumberFormatInput
                  disabled={isReadOnly}
                  type="text"
                  customInput={Input}
                  value={
                    changedKYC.AccountKYCQuantityDepositedStocks ||
                    changedKYC.AccountKYCQuantityDepositedStocks === ""
                      ? changedKYC.AccountKYCQuantityDepositedStocks
                      : KYC.AccountKYCQuantityDepositedStocks
                  }
                  onValueChange={(e) => onChange("AccountKYCQuantityDepositedStocks", e.value)}
                  dataTypeDecimalName="quantity"
                />
              </CustomFormInput>
            </Col>
            <Col>
              <CustomFormInput label="Quantity Withdraw Assets: ">
                <NumberFormatInput
                  disabled={isReadOnly}
                  type="text"
                  customInput={Input}
                  value={
                    changedKYC.AccountKYCQuantityRetiredStocks ||
                    changedKYC.AccountKYCQuantityRetiredStocks === ""
                      ? changedKYC.AccountKYCQuantityRetiredStocks
                      : KYC.AccountKYCQuantityRetiredStocks
                  }
                  onValueChange={(e) => onChange("AccountKYCQuantityRetiredStocks", e.value)}
                  dataTypeDecimalName="quantity"
                />
              </CustomFormInput>
            </Col>
          </Row>
          <Row>
            <Col>
              <CustomFormInput label="Amount Maximum Custody: ">
                <NumberFormatInput
                  disabled={isReadOnly}
                  type="text"
                  customInput={Input}
                  value={
                    changedKYC.AccountKYCAmountMaximumCustody ||
                    changedKYC.AccountKYCAmountMaximumCustody === ""
                      ? changedKYC.AccountKYCAmountMaximumCustody
                      : KYC.AccountKYCAmountMaximumCustody
                  }
                  onValueChange={(e) => onChange("AccountKYCAmountMaximumCustody", e.value)}
                  dataTypeDecimalName="currency"
                />
              </CustomFormInput>
            </Col>
            <Col>
              <CustomFormInput label="Manual High Risk: ">
                <Input
                  disabled={isReadOnly}
                  type="select"
                  value={
                    changedKYC.AccountKYCManualHighRisk
                      ? changedKYC.AccountKYCManualHighRisk
                      : KYC.AccountKYCManualHighRisk
                  }
                  onChange={(e) => onChange("AccountKYCManualHighRisk", e.target.value)}
                >
                  <option value={false}>No</option>
                  <option value={true}>Yes</option>
                </Input>
              </CustomFormInput>
            </Col>
          </Row>

          <CustomFormInput label="Comments: ">
            <Input
              disabled={isReadOnly}
              type="textarea"
              value={
                changedKYC.AccountKYCComments || changedKYC.AccountKYCComments === ""
                  ? changedKYC.AccountKYCComments
                  : KYC.AccountKYCComments
              }
              onChange={(e) => onChange("AccountKYCComments", e.target.value)}
            />
          </CustomFormInput>
        </FormGroup>
      </Form>
      {mode !== AccountMode.New && !isReadOnly && (
        <Button style={{ marginRight: 10 }} disabled={loadingSave} onClick={() => revert()}>
          <i className="fa fa-undo" /> Revert
        </Button>
      )}
      {!isReadOnly && (
        <LaddaButton
          className="btn btn-ladda btn-primary"
          loading={loadingSave}
          disabled={Object.keys(changedKYC).length === 0}
          onClick={() => handleSave()}
          data-style={EXPAND_RIGHT}
        >
          Save
        </LaddaButton>
      )}
{' '}
{mode !== AccountMode.New && showDownload && (
  <LaddaButton
    onClick={() => getKYCReport()}
    className="btn btn-ladda btn-primary"
    style={{ marginRight: 10 }}
    loading={downloading}
    data-style={EXPAND_RIGHT}
    disabled={downloading}
  >
    <i className="fa fa-download" style={{ marginRight: 5 }} />
    {'Download report'}
  </LaddaButton>
)}

{mode !== AccountMode.New && file && (
  <Button
    onClick={() => window.open(file, '_blank')}
    title="Download generated file"
  >
    <i className="fa fa-download" />
  </Button>
)}

    </div>
  );
};

KYCTab.propTypes = {
  accountId: PropTypes.string.isRequired,
  KYC: PropTypes.object,
  setAccountData: PropTypes.func.isRequired,
  services: PropTypes.object.isRequired,
  account: PropTypes.object.isRequired,
  mode: PropTypes.object.isRequired,
};

KYCTab.defaultProps = {
  KYC: undefined,
};

export default KYCTab;
